/**
 * NOTE:(diwakersurya) This file provides convenience hooks for using tanstack query along
 * with our custom api calling mechanism see redux-api-v2.js. This  will we enough for probably
 * 90% of the cases. . That being said, There will be cases
 * where it may not be sufficient to use the hooks provided here. In such cases, you can use the
 * tanstack queries directly. But keep in mind that you will have to use something like redux-api-v2.js
 * or redux-api-bff.js to make the api calls and manage things like aborting requests, handling errors,
 * etc. manually.
 */
import * as React from 'react';
import {
  useQuery,
  useSuspenseQuery,
  useQueryClient,
  useSuspenseQueries,
} from '@tanstack/react-query';
import {useState, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {captureApiError, ApiError} from 'src/utils/errors';
import {camel} from 'src/utils/camel-snake';
import {serializeQuery} from 'src/utils';
import {defaultState, type ResourceState} from './useResource';
import {baseRequest} from 'src/utils/redux-api-v2';


export const IGNORED_STATUS_CODES = [401, 404] as const;

export type {ResourceState};
type Query = {
  [key: string]: string;
};

export type BaseApiOptions = {
  method?: 'GET';
  onFetch?: () => unknown;
  handleError?: (arg1: ApiError) => unknown;
  shouldFetch?: boolean;
  shouldCamel?: boolean;
  cacheTimeout?: number;
  apiPath?: string;
  deps?: unknown[];
  key?: string | Array<string>;
  // all 400 <= status < 500 codes get logged _to sentry_
  // unless they're included here. We don't log 401 or 404s.
  // only update this to maintain a semantic api contract
  // but to avoid spamming sentry.
  ignoredStatusCodes?: Array<number>;
};
export type ApiUrl =
  | string
  | {
      pathname: string;
      query?: Query;
    }
  | null
  | undefined;
export type ApiUrlArg = ApiUrl | (() => ApiUrl);

type Refetch = (force?: boolean) => void;

// Helper to normalize URL
const normalizeUrl = (
  url: ApiUrl,
): {
  pathname: string | null | undefined;
  query: Query | null | undefined;
} => {
  if (!url) {
    return {pathname: null, query: null};
  }
  if (typeof url === 'string') {
    return {pathname: url, query: null};
  }
  return {
    pathname: url.pathname,
    query: url.query || null,
  };
};

type ExtendedApiOptions<Response> = BaseApiOptions & {
  handleResponse?: (arg1: Response) => unknown;
};

/**
 * @deprecated will be removed. It is there for compatibility with old code.
 * Please migrate to useApiQuery instead.
 */
export function useAdvancedApi<Response>(
  url: ApiUrl,
  {
    method = 'GET',
    key,
    onFetch,
    handleResponse,
    handleError,
    shouldFetch = true,
    shouldCamel,
    cacheTimeout,
    // @ts-expect-error - TS4104 - The type 'readonly [401, 404]' is 'readonly' and cannot be assigned to the mutable type 'number[]'.
    ignoredStatusCodes = IGNORED_STATUS_CODES,
    deps,
    ...options
  }: ExtendedApiOptions<Response>,
): Refetch {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const {pathname, query} = normalizeUrl(url);

  //NOTE:(diwakersurya) if key is an array, it will be used as the query key

  const queryKey = key
    ? Array.isArray(key)
      ? key
      : [key]
    : [pathname, query, method];

  const queryFn = async ({
    signal,
  }: {
    signal?: AbortSignal;
  }): Promise<Response | null | undefined> => {
    if (!pathname || !shouldFetch) {
      return null;
    }

    try {
      onFetch?.();
      const response = await dispatch(
        baseRequest(pathname, {
          method,
          query: query || undefined,
          signal,
          options: {
            ...options,
            handleAuth: true,
          },
        }),
      );
      const result = shouldCamel ? camel(response) : response;
      handleResponse?.(result);
      return result;
    } catch (error: any) {
      if (error instanceof ApiError) {
        const {status} = error.response;
        if (status < 500 && !ignoredStatusCodes.includes(status)) {
          captureApiError(error);
        }
        handleError?.(error);
      }
      throw error;
    }
  };

  const {refetch} = useQuery({
    queryKey,
    queryFn,
    enabled: Boolean(pathname && shouldFetch),
    staleTime: cacheTimeout,
    gcTime: cacheTimeout, // v5: cacheTime -> gcTime
  });

  return (force?: boolean) => {
    if (force) {
      // @ts-expect-error - TS2559 - Type '(string | Query | null | undefined)[]' has no properties in common with type 'QueryFilters<readonly unknown[]>'.
      queryClient.removeQueries(queryKey);
    }
    refetch();
  };
}

export type ApiState<Response> = ResourceState<Response> & {
  refetch: () => void;
};

/**
 * @deprecated will be removed. It is there for compatibility with old code.
 * Please migrate to useApiQuery instead.
 */
export function useApi<Response>(
  url: ApiUrl,
  optionsArg?: ExtendedApiOptions<Response> | Array<unknown>,
): ApiState<Response> {
  const [state, setState] = useState<ResourceState<Response>>({
    ...defaultState,
    isLoading: Boolean(url),
  });

  const options = {
    onFetch: () => {
      setState((state) => ({...state, isLoading: true}));
    },
    handleResponse: (result: Response) => {
      setState({
        result,
        error: null,
        isLoading: false,
      });
    },
    handleError: (error: Error) => {
      setState({
        result: null,
        error,
        isLoading: false,
      });
    },
  } as const;

  const refetch = useAdvancedApi<Response>(
    url,
    Array.isArray(optionsArg)
      ? {
          ...options,
          deps: optionsArg,
        }
      : {
          ...optionsArg,
          ...options,
        },
  );

  return {
    ...state,
    refetch,
  };
}

export function useApiStub<Stub>(stub: Stub): ApiState<Stub> {
  return {
    ...defaultState,
    isLoading: false,
    result: stub,
    refetch: () => {
      // pass
    },
  };
}

/**
 * @deprecated will be removed. It is there for compatibility with old code.
 * Please migrate to useApiQuery instead.
 */
export function useLegacyApi<Response>(
  url: ApiUrl,
  options?: ExtendedApiOptions<Response>,
): Response | null | undefined {
  const [response, setResponse] = useState<Response | null | undefined>(null);

  useAdvancedApi<Response>(url, {
    ...options,
    handleResponse: (response) => {
      setResponse(response);
    },
  });

  return response;
}
/**
 * @deprecated will be removed. It is there for compatibility with old code.
 * Please migrate to useApiQuery instead.
 */

export function useLegacyApiWithParams<Response>(
  getUrl: () => ApiUrl,
  params: Array<unknown>,
  options?: ExtendedApiOptions<Response>,
): Response | null | undefined {
  const url = useMemo(getUrl, params);
  return useLegacyApi(url, options);
}
/**
 * @deprecated will be removed. It is there for compatibility with old code.
 * Please migrate to useApiQuery instead.
 */
export function useLoadingApi<Response>(
  getUrl: () => ApiUrl,
  params: Array<unknown>,
  options?: ExtendedApiOptions<Response>,
): {
  response: Response | null | undefined;
  loaded: boolean;
} {
  const response = useLegacyApiWithParams(getUrl, params, options);
  return {response, loaded: response != null};
}

/**
 * Hook that combines useAdvancedApi functionality with React Query options
 * @template Response The expected response type
 * @param {ApiUrl} url The API URL to fetch from
 * @param {Object} options Combined options for API and React Query
 * @param {Object} options.queryOptions React Query specific options (like enabled, staleTime etc)
 * @param {Object} options.apiOptions Options for the API call (like method, query etc)
 * @returns {Object} React Query result object with data, error, isLoading etc
 */

export type ApiOptions = {
  handleAuth?: boolean;
  // TODO (kyle): this property is too multi-purpose
  // Don't print stuff to console, modals, etc.
  silence?: boolean;
  redirect?: 'follow' | 'manual' | 'error';
  apiPath?: string;
  headers?: {
    [key: string]: string;
  };
};
export type BaseApiRequestOptions = {
  signal?: AbortSignal;
  options?: Partial<ApiOptions>;
  method?: 'GET' | 'POST';
  data?: unknown;
};

export type QueryResult<TData> = {
  data: TData | null | undefined;
  error: Error | null | undefined;
  isLoading: boolean; // v5: remains same for backward compatibility;
  isPending: boolean; // v5: new property for initial load;
  isError: boolean;
  isFetching: boolean; // v5: remains same for background refetches;
  refetch: () => Promise<void>;
  remove: () => void;
};

export function useApiInternalQuery<Response>(
  url: ApiUrl,
  {
    queryOptions = {},
    apiOptions = {},
  }: {
    queryOptions?: {
      [key: string]: unknown;
    };
    apiOptions?: BaseApiRequestOptions;
  },
  queryMethod: typeof useSuspenseQuery | typeof useQuery,
): QueryResult<Response> {
  const dispatch = useDispatch();
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const aborter = React.useRef();

  // Extract API related options
  const {
    signal: userProvidedAbortSignal,
    options = {},
    method = 'GET',
    data,
  } = apiOptions;
  const {enabled, ...restOptions} = queryOptions;

  const {pathname, query} = normalizeUrl(url);
  if (enabled && !pathname) {
    throw new Error('Pathname is required');
  }
  // Create query key based on URL and query params
  const queryKey = useMemo(
    () => [pathname, query].filter(Boolean),
    [pathname, JSON.stringify(query)],
  );

  // Define fetch function for React Query
  const fetchData = React.useCallback(
    async ({signal}: {signal?: AbortSignal}) => {
      // NOTE:(diwakersurya) when the user has not provided abort signal or it is not controlled through react query, we will
      // go ahead and create a new abort controller which aborts the previous request when a new request is
      // made.
      if (aborter.current) {
        // @ts-expect-error - TS2339 - Property 'abort' does not exist on type '{}'.
        aborter.current.abort();
        aborter.current = null;
      }

      const abortSignal = userProvidedAbortSignal ?? signal;
      if (!abortSignal) {
        const abortController = new AbortController();
        aborter.current = abortController;
      }

      if (!pathname) {
        throw new Error('Pathname is required');
      }
      try {
        const response = await dispatch(
          baseRequest(pathname, {
            method,
            query: query || undefined,
            data: method === 'GET' ? undefined : data || undefined,
            signal,
            options: {
              ...options,
              signal: abortSignal,
            },
          }),
        );
        return response;
      } catch (error: any) {
        throw error;
      }
    },
    [url, query, options, data, method],
  );

  // Use React Query's method with combined options
  // @ts-expect-error - TS2322 - Type 'QueryObserverRefetchErrorResult<ThunkAction<any>, Error> | QueryObserverSuccessResult<ThunkAction<any>, Error> | ... 4 more ... | Omit<...>' is not assignable to type 'QueryResult<Response>'.
  return queryMethod({
    queryKey,
    queryFn: fetchData,
    enabled: Boolean(url),
    ...queryOptions,
  });
}

/**
 * use this wherever you want to leverage the query mechanism but not the suspense mechanism.
 */
export function useApiQuery<Response>(
  url: ApiUrl,
  {
    queryOptions = {},
    apiOptions = {},
  }: {
    queryOptions?: {
      [key: string]: unknown;
    };
    apiOptions?: BaseApiRequestOptions;
  },
): QueryResult<Response> {
  return useApiInternalQuery(url, {queryOptions, apiOptions}, useQuery);
}

/**
 * use this wherever you want to leverage the suspense query mechanism.
 */
export function useApiSuspenseQuery<Response>(
  url: ApiUrl,
  {
    queryOptions = {},
    apiOptions = {},
  }: {
    queryOptions?: {
      [key: string]: unknown;
    };
    apiOptions?: BaseApiRequestOptions;
  },
): QueryResult<Response> {
  return useApiInternalQuery(url, {queryOptions, apiOptions}, useSuspenseQuery);
}

export function useApiSuspenseQueries<Response = any>(
  queries: Array<{
    url: ApiUrl;
    queryOptions?: {
      [key: string]: unknown;
    };
    apiOptions?: BaseApiRequestOptions;
  }>,
): Array<QueryResult<Response>> {
  const dispatch = useDispatch();

  const processedQueries = React.useMemo(() => {
    return queries.map(({url, queryOptions = {}, apiOptions = {}}) => {
      const {
        signal: userProvidedAbortSignal,
        options = {},
        method = 'GET',
        data,
      } = apiOptions;

      const {pathname, query} = normalizeUrl(url);

      const queryKey = Array.isArray(queryOptions.queryKey)
        ? [...queryOptions.queryKey]
        : [pathname, query].filter(Boolean);

      const fetchData = async ({signal}: {signal?: AbortSignal}) => {
        if (!pathname) {
          throw new Error('Pathname is required');
        }

        try {
          const response = await dispatch(
            baseRequest(pathname, {
              method,
              query: query || undefined,
              data: method === 'GET' ? undefined : data || undefined,
              signal: userProvidedAbortSignal ?? signal,
              options: {
                ...options,
              },
            }),
          );
          return response;
        } catch (error: any) {
          if (error instanceof ApiError) {
            captureApiError(error);
          }
          throw error;
        }
      };

      return {
        queryKey,
        queryFn: fetchData,
        enabled: Boolean(pathname),
        ...queryOptions,
      };
    });
  }, [queries, dispatch]);

  return useSuspenseQueries({
    queries: processedQueries,
  });
}
